import { Box, ButtonBase, makeStyles, Typography } from "@material-ui/core";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";

import { CustomButton } from "@/components/Button";
import { Chevron } from "@/components/Chevron";
import {
  ExclusiveFetchError,
  ExclusivePageHeading,
  ExclusiveTemplate,
  useExclusiveLayout,
} from "@/components/exclusive/ExclusiveContentLayout";
import { LimitedPropertyCard } from "@/components/exclusive/LimitedPropertyCard";
import { Loading } from "@/components/Loading";
import { LimitedPropertyWithAgent } from "@/components/property/LimitedPropertiesSection";
import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useGetLimitedProperties } from "@/hooks/useGetLimitedProperties";
import theme from "@/theme";
import { NavigationPath } from "@/utilities/enums";
import { calculatePaginationInvestmentProposal } from "@/utilities/pagination";

const useStyles = makeStyles({
  list: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: layout.space[12],
    [theme.breakpoints.up("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: layout.space[24] },
  },
  description: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginBottom: layout.space[12],
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  pagination: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: layout.space[12],
    marginTop: layout.space[32],
  },
  pages: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: layout.space[4] },
  page: {
    width: 36,
    height: 36,
    borderRadius: layout.radius.full,
    color: colors["text/secondary"],
    ...textStyles["Number/XS"],
  },
  selected: { backgroundColor: colors["action/primary"], color: colors["text/inverse"] },
  arrow: {
    border: `1px solid ${colors["border/default"]}`,
    backgroundColor: colors["surface/card"],
    "&:disabled": { opacity: 0.35 },
  },
  empty: {
    minHeight: "calc(100vh - 184px)",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("md")]: { minHeight: 0 },
  },
  emptyCard: {
    padding: `${layout.space[32]}px ${layout.space[24]}px`,
    display: "flex",
    flexDirection: "column",
    gap: layout.space[16],
    [theme.breakpoints.up("md")]: { padding: `${layout.space[40]}px ${layout.space[48]}px` },
  },
});

const LIMITED_PROPERTIES_PER_PAGE = 10;
// 詳細画面から戻ったときにスクロール位置を復元するための保存キー
export const LIMITED_PROPERTIES_SCROLL_STORAGE_KEY = "limited_properties_list_scroll_y";

export const LimitedPropertiesListPage: React.FC = () => {
  const classes = useStyles();
  const common = useExclusiveLayout();
  const history = useHistory();
  const location = useLocation();
  const { limitedProperties, loading, error, refresh } = useGetLimitedProperties({ shuffle: false });
  // 詳細画面から戻ったときにページを維持するため、ページ番号は URL クエリで保持する
  const queryPage = Number(new URLSearchParams(location.search).get("page")) || 1;
  const [currentPage, setCurrentPage] = React.useState(queryPage);

  const { currentInvestmentProposals: currentLimitedProperties, totalPages } = calculatePaginationInvestmentProposal(
    limitedProperties,
    currentPage,
    LIMITED_PROPERTIES_PER_PAGE
  );

  const handlePageChange = React.useCallback(
    (page: number) => {
      setCurrentPage(page);
      const params = new URLSearchParams(location.search);
      params.set("page", String(page));
      history.replace({ search: params.toString() });
    },
    [history, location.search]
  );

  // 件数が減ってクエリのページが存在しなくなった場合は最終ページに寄せる（空表示の防止）
  React.useEffect(() => {
    if (loading || totalPages === 0 || currentPage <= totalPages) return;
    handlePageChange(totalPages);
  }, [loading, totalPages, currentPage, handlePageChange]);

  const handleOpenDetail = React.useCallback(
    (property: LimitedPropertyWithAgent) => {
      sessionStorage.setItem(LIMITED_PROPERTIES_SCROLL_STORAGE_KEY, String(window.scrollY));
      history.push("/limited_properties/detail", { property });
    },
    [history]
  );

  // 一覧の描画が終わってからスクロール位置を復元する
  // （app.tsx の ScrollToTop は pathname 変更時に先頭へ戻すため、描画後のこの復元が最後に適用される）
  const hasRestoredScroll = React.useRef(false);
  React.useLayoutEffect(() => {
    if (loading || hasRestoredScroll.current) return;
    hasRestoredScroll.current = true;

    const savedScrollY = sessionStorage.getItem(LIMITED_PROPERTIES_SCROLL_STORAGE_KEY);
    sessionStorage.removeItem(LIMITED_PROPERTIES_SCROLL_STORAGE_KEY);
    // 戻る操作（端末バック・ブラウザバック）のときだけ復元する
    if (savedScrollY === null || history.action !== "POP") return;

    const scrollY = Number(savedScrollY);
    if (!Number.isFinite(scrollY) || scrollY <= 0) return;
    window.scrollTo(0, scrollY);
  }, [loading, history.action]);

  return (
    <ExclusiveTemplate
      headerTitle="限定公開物件"
      navigationPath={NavigationPath.HOME}
      onBack={() => history.goBack()}
      isDense
      isPcV2
    >
      <Box className={common.body}>
        <ExclusivePageHeading
          title="限定公開物件"
          backLabel="ホーム"
          onBack={() => history.push("/mypage")}
          subtitle={
            !loading && limitedProperties.length > 0
              ? `${limitedProperties.length}件 ・ プランナーが扱う、一般には出ていない物件です`
              : undefined
          }
        />
        {loading ? (
          <Loading />
        ) : limitedProperties.length === 0 ? (
          error ? (
            <ExclusiveFetchError message="限定公開物件の読み込みに失敗しました。" onRetry={refresh} />
          ) : (
            <Box className={classes.empty}>
              <Box className={common.empty}>
                <Box className={`${common.card} ${classes.emptyCard}`}>
                  <Typography component="h2" className={common.emptyTitle}>
                    いまは公開されている物件がありません
                  </Typography>
                  <Typography className={common.text}>
                    プランナーが扱う物件のうち、一般には出していないものがここに並びます。相性の良いプランナーとつながると、届く物件が増えます。
                  </Typography>
                  <CustomButton customVariant="filled" onClick={() => history.push("/agent_search")}>
                    相性の良いプランナーを見る
                  </CustomButton>
                </Box>
              </Box>
            </Box>
          )
        ) : (
          <>
            {error && (
              <ExclusiveFetchError
                message="再読み込みに失敗しました。取得済みの物件を表示しています。"
                onRetry={refresh}
              />
            )}
            <Typography className={classes.description}>
              {limitedProperties.length}件 ・ プランナーが扱う、一般には出ていない物件です
            </Typography>
            <Box className={classes.list}>
              {currentLimitedProperties.map((property, index) => (
                <LimitedPropertyCard
                  key={`${property.property_name ?? "property"}-${index}`}
                  property={property}
                  onClick={() => handleOpenDetail(property)}
                />
              ))}
            </Box>
            {totalPages > 1 && (
              <Box className={classes.pagination}>
                <Box component="nav" aria-label="限定公開物件のページ送り" className={classes.pages}>
                  <ButtonBase
                    aria-label="前のページ"
                    className={`${classes.page} ${classes.arrow}`}
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <Chevron direction="back" />
                  </ButtonBase>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <ButtonBase
                      key={page}
                      aria-label={`${page}ページ`}
                      aria-current={currentPage === page ? "page" : undefined}
                      className={`${classes.page} ${currentPage === page ? classes.selected : ""}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </ButtonBase>
                  ))}
                  <ButtonBase
                    aria-label="次のページ"
                    className={`${classes.page} ${classes.arrow}`}
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <Chevron />
                  </ButtonBase>
                </Box>
                <Typography className={common.small}>
                  {limitedProperties.length}件中 {(currentPage - 1) * LIMITED_PROPERTIES_PER_PAGE + 1}〜
                  {Math.min(currentPage * LIMITED_PROPERTIES_PER_PAGE, limitedProperties.length)}件
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </ExclusiveTemplate>
  );
};
